/**
 * Ausklappfenster der Kopfzeile (F-42, F-44): am Laptop ein Fenster unter
 * dem Knopf, am Handy ein Panel, das von unten hochfaehrt - dort verdeckt
 * es nichts halb, ist mit dem Daumen erreichbar und hat einen abgedunkelten
 * Hintergrund. Schliesst mit Escape, mit einem Klick daneben und ueber den
 * Hintergrund; der ausloesende Knopf selbst zaehlt nicht als "daneben",
 * damit er sauber auf- und zuklappt.
 *
 * Am Handy wird das Panel direkt in <body> gehaengt: die Kopfzeile hat
 * `backdrop-blur`, und ein Filter macht sie zum Bezugsrahmen fuer `fixed` -
 * das Panel klebte sonst an der Kopfzeile statt am unteren Bildschirmrand.
 *
 * Am Handy ist das Panel modal: Tab bleibt im Panel, und geschlossen wird
 * ueber das `click` auf dem Hintergrund - nicht schon beim `pointerdown`.
 * Sonst verschwaende der Hintergrund vor dem Klick der Tipp-Geste, und der
 * Klick traefe das Element darunter (z. B. eine Aufgabe zum Abhaken).
 *
 * Der Aufrufer stellt den Knopf und ein `relative`-Elternelement bereit und
 * gibt den Fokus beim Schliessen an den Knopf zurueck.
 */
import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/lib/utils/cn';

/** Unterhalb von Tailwinds `sm` (640 px) ist es ein Handy. */
const HANDY = '(max-width: 639.98px)';

const FOKUSSIERBAR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Tab und Umschalt+Tab im Panel im Kreis fuehren. */
function fokusImKreis(e: KeyboardEvent, box: HTMLElement) {
  const ziele = [...box.querySelectorAll<HTMLElement>(FOKUSSIERBAR)];
  if (ziele.length === 0) { e.preventDefault(); return; }
  const erstes = ziele[0]!, letztes = ziele[ziele.length - 1]!;
  const aktiv = document.activeElement;
  if (e.shiftKey && (aktiv === erstes || aktiv === box || !box.contains(aktiv))) { e.preventDefault(); letztes.focus(); }
  else if (!e.shiftKey && (aktiv === letztes || !box.contains(aktiv))) { e.preventDefault(); erstes.focus(); }
}

function useIstHandy(): boolean {
  const [handy, setHandy] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(HANDY).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(HANDY);
    if (!mq) return;
    const wechsel = () => setHandy(mq.matches);
    mq.addEventListener('change', wechsel);
    return () => mq.removeEventListener('change', wechsel);
  }, []);
  return handy;
}

export function Ausklapper({ label, ausloeser, onClose, className, lage = 'right-0 top-11', children }: {
  /** Name des Fensters fuer Screenreader. */
  label: string;
  /** Der Knopf, der das Fenster oeffnet - ein Klick darauf schliesst nicht doppelt. */
  ausloeser: RefObject<HTMLElement | null>;
  onClose: () => void;
  /** Zusaetzliche Klassen fuer das Fenster am Laptop, z. B. die Breite (`w-[26rem]`). */
  className?: string;
  /** Lage am Laptop relativ zum Elternelement; Standard: unter einem Knopf der Kopfzeile. */
  lage?: string;
  children: ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);
  const hintergrund = useRef<HTMLDivElement>(null);
  const handy = useIstHandy();

  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'Tab' && handy && box.current) fokusImKreis(e, box.current);
    };
    const klick = (e: PointerEvent) => {
      const ziel = e.target as Node;
      // Der Hintergrund schliesst selbst per `click` (siehe oben).
      if (box.current?.contains(ziel) || ausloeser.current?.contains(ziel) || hintergrund.current?.contains(ziel)) return;
      onClose();
    };
    window.addEventListener('keydown', taste);
    window.addEventListener('pointerdown', klick);
    return () => { window.removeEventListener('keydown', taste); window.removeEventListener('pointerdown', klick); };
  }, [onClose, ausloeser, handy]);

  // Fokus ins Fenster, damit Tastatur und Screenreader dort weitermachen.
  useEffect(() => { box.current?.focus({ preventScroll: true }); }, [handy]);

  if (!handy) {
    return (
      <div ref={box} role="dialog" aria-label={label} tabIndex={-1}
        className={cn('absolute z-50 flex max-h-[75vh] flex-col overflow-hidden rounded-lg border border-line bg-surface shadow-2xl outline-none', lage, className)}>
        {children}
      </div>
    );
  }

  return createPortal(
    <>
      <div ref={hintergrund} aria-hidden="true" onClick={onClose} className="fixed inset-0 z-40 bg-black/40" />
      <div ref={box} role="dialog" aria-modal="true" aria-label={label} tabIndex={-1}
        className="fixed inset-x-0 bottom-0 z-50 flex max-h-[85vh] flex-col overflow-hidden rounded-t-xl border border-b-0 border-line bg-surface pb-[env(safe-area-inset-bottom)] shadow-2xl outline-none motion-safe:animate-hochfahren">
        {/* Griff: zeigt, dass es ein Panel ist. */}
        <span aria-hidden="true" className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-line-strong" />
        {children}
      </div>
    </>,
    document.body,
  );
}
